import * as React from 'react';

import { Text } from '@/components/ui';

// DescriptionList — declarative key/value list for read-only detail
// surfaces (the typical "Show" Dialog: id, name, timestamps, joined
// related collections, etc).
//
// Each entry pairs a muted label with the value beneath it. Values are
// rendered through the design-system `Text` primitive so consumers don't
// own typography or colour — the only escape hatch is `tone` per item,
// useful for hinting at warnings (e.g. a destructive tone on a legacy
// rate caption).
//
// The component renders semantic `<dl>` / `<dt>` / `<dd>` so screen
// readers announce the label/value pairing correctly without an extra
// wrapper.
//
// Items API:
//   - label   ReactNode  — what the value represents.
//   - value   ReactNode  — the value itself. When empty / null /
//                          undefined we render an em-dash so the row
//                          keeps a stable visual rhythm.
//   - tone    string     — optional override for the value's typography
//                          tone (default `foreground`). Useful for
//                          surfacing warnings.
//
// Layout: a tight vertical stack of label/value pairs. No grid, no
// columns — long values wrap inside the row instead of overflowing.

const VALUE_FALLBACK = '—';

// Mirror ContentHeader: labels often arrive from the glossary
// (`glossary.token` → "token") so the same lowercase noun is reusable
// mid-sentence elsewhere. We promote only the first character here so
// rows read as proper labels. Non-string labels (ReactNode) pass
// through untouched.
const capitalizeFirst = value => {
    if (typeof value !== 'string' || value.length === 0) {
        return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const DescriptionList = React.forwardRef(({ className: _className, items = [], style: _style, ...props }, ref) => {
    return (
        <dl
            ref={ref}
            className="tw-m-0 tw-flex tw-flex-col tw-gap-4"
            {...props}
        >
            {items.map((item, index) => {
                const value = item.value === null || item.value === undefined || item.value === ''
                    ? VALUE_FALLBACK
                    : item.value;
                const tone = item.tone ?? 'foreground';
                // `<dt>` and `<dd>` are structural — Text's allowlist is
                // restricted to inline text elements (span, p, label…),
                // so the structural shell stays as plain HTML and Text
                // owns typography inside each cell. `dd` carries a
                // `tw-m-0` to drop the default UA left margin.
                return (
                    <div key={item.key ?? index} className="tw-flex tw-flex-col tw-gap-1">
                        <dt>
                            <Text tone="muted" variant="caption">
                                {capitalizeFirst(item.label)}
                            </Text>
                        </dt>
                        <dd className="tw-m-0">
                            <Text tone={tone} variant="body">
                                {value}
                            </Text>
                        </dd>
                    </div>
                );
            })}
        </dl>
    );
});

DescriptionList.displayName = 'DescriptionList';

export { DescriptionList };
